/**
 * Compositional Dialog primitives — shadcn-style.
 *
 * --------------------------------------------------------------------------
 * The convention (read this before adding a new modal)
 * --------------------------------------------------------------------------
 *
 * Each feature exports a `XxxContent` component that returns ONLY a
 * `<DialogContent>` body. Consumers compose `<Dialog>` + `<DialogTrigger>` +
 * `<XxxContent>` at the point of use. The hook layer never owns modal
 * open/close state — that's a presentation concern and lives in `useState`
 * right next to the JSX.
 *
 * --------------------------------------------------------------------------
 * Patterns
 * --------------------------------------------------------------------------
 *
 * 1) Uncontrolled — the common case. Single trigger, no external coordination.
 *
 *    <Dialog>
 *      <DialogTrigger asChild>
 *        <Button>Assign</Button>
 *      </DialogTrigger>
 *      <AssignProcedureContent workOrderId={id} machineId={mid} />
 *    </Dialog>
 *
 *    Dialog manages its own open state internally. DialogTrigger flips it.
 *    DialogClose / X button / Escape / overlay-click flip it back. Done.
 *
 * 2) Controlled — when something outside the Dialog tree also needs to open
 *    it. E.g. a row click that opens the assign modal pre-set to a part.
 *
 *    const [open, setOpen] = useState(false);
 *    const [previewPart, setPreviewPart] = useState<Part | null>(null);
 *
 *    <Dialog open={open} onOpenChange={setOpen}>
 *      <DialogTrigger asChild>
 *        <AssignPartButton />
 *      </DialogTrigger>
 *      <AssignPartContent initialPreviewPart={previewPart} ... />
 *    </Dialog>
 *
 *    <PartRow onPreview={(p) => { setPreviewPart(p); setOpen(true); }} />
 *
 *    DialogTrigger still works in controlled mode (calls onOpenChange).
 *
 * 3) Target pattern — for "this modal opens when a target object is set".
 *    Avoids having two pieces of state (`isOpen` + `target`).
 *
 *    const [editTarget, setEditTarget] = useState<{ part; qty } | null>(null);
 *
 *    <Dialog
 *      open={!!editTarget}
 *      onOpenChange={(o) => !o && setEditTarget(null)}
 *    >
 *      <EditPartContent target={editTarget} onSubmit={...} />
 *    </Dialog>
 *
 *    Row dispatches: onEdit={(part, qty) => setEditTarget({ part, qty })}
 *
 *    IMPORTANT: don't conditionally render the content (`{target && <X/>}`).
 *    Unmounting the Content the moment `target` goes null kills the exit
 *    animation. Instead, render the Content unconditionally and have it
 *    capture the last non-null target with a ref:
 *
 *      export function EditPartContent({ target, ...rest }) {
 *        const ref = useRef<Target | null>(null);
 *        if (target) ref.current = target;
 *        const captured = ref.current;
 *        return (
 *          <DialogContent>
 *            {captured && <Body key={captured.id} initial={captured} ... />}
 *          </DialogContent>
 *        );
 *      }
 *
 *    The `key` forces a fresh remount on each new target so `useState(() =>
 *    initial)` captures the right values. During close, target goes null but
 *    `ref.current` keeps the previous value, so Body keeps rendering with
 *    stable data while DialogContent animates out.
 *
 * --------------------------------------------------------------------------
 * Lazy-mount guarantee
 * --------------------------------------------------------------------------
 *
 * `DialogContent` returns null while the dialog is closed, so any queries /
 * mutations / heavy state inside its children only mount when the user opens
 * the modal. Use this — don't pre-fetch modal data outside the modal.
 *
 * --------------------------------------------------------------------------
 * State-during-close
 * --------------------------------------------------------------------------
 *
 * DialogContent keeps its children rendered for ~180ms during the exit
 * animation. If view-driving state (which tab is selected, which part is
 * previewed) lives in a parent that resets on close, the modal will flicker
 * to the "wrong" view mid-animation. Solve this by capturing initial values
 * INSIDE the content via `useState(() => initialValue)` — local state stays
 * stable until unmount.
 */
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import { X } from "phosphor-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export type DialogSize = "normal" | "small";

type DialogContextValue = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titleId: string;
  descriptionId: string;
  showCloseButton: boolean;
  size: DialogSize;
};

const DialogContext = createContext<DialogContextValue | null>(null);

// Nesting depth so stacked dialogs (a dialog opened from inside another) get
// higher z-index — otherwise the inner overlay sits below the outer panel and
// fails to dim it. Top-level dialog = depth 1.
const DialogDepthContext = createContext(0);

export function useDialogContext() {
  const ctx = useContext(DialogContext);
  if (!ctx)
    throw new Error("Dialog sub-components must be rendered inside <Dialog>");
  return ctx;
}

const FOCUSABLE_SELECTOR =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

// ---------------------------------------------------------------------------
// Dialog (root)
// ---------------------------------------------------------------------------

export type DialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  showCloseButton?: boolean;
  /** Visual density — drives header/footer padding per Figma spec. @default "normal" */
  size?: DialogSize;
  children: React.ReactNode;
};

export function Dialog({
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
  showCloseButton = true,
  size = "normal",
  children,
}: DialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? (controlledOpen as boolean) : uncontrolledOpen;
  const id = useId();
  const depth = useContext(DialogDepthContext) + 1;

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  return (
    <DialogDepthContext.Provider value={depth}>
      <DialogContext.Provider
        value={{
          open,
          onOpenChange: handleOpenChange,
          titleId: `${id}-title`,
          descriptionId: `${id}-desc`,
          showCloseButton,
          size,
        }}
      >
        {children}
      </DialogContext.Provider>
    </DialogDepthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// DialogTrigger
// ---------------------------------------------------------------------------

export type DialogTriggerProps = {
  asChild?: boolean;
  children: React.ReactElement;
};

export function DialogTrigger({
  children,
  asChild = false,
}: DialogTriggerProps) {
  const { onOpenChange } = useDialogContext();
  const open = () => onOpenChange(true);

  if (asChild) {
    return React.cloneElement(children, {
      onClick: (e: React.MouseEvent) => {
        children.props.onClick?.(e);
        open();
      },
    });
  }

  return (
    <button type="button" onClick={open}>
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// DialogClose
// ---------------------------------------------------------------------------

export type DialogCloseProps = {
  asChild?: boolean;
  children: React.ReactElement;
};

export function DialogClose({ children, asChild = false }: DialogCloseProps) {
  const { onOpenChange } = useDialogContext();
  const close = () => onOpenChange(false);

  if (asChild) {
    return React.cloneElement(children, {
      onClick: (e: React.MouseEvent) => {
        children.props.onClick?.(e);
        close();
      },
    });
  }

  return (
    <button type="button" onClick={close}>
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// DialogContent
// ---------------------------------------------------------------------------

export type DialogContentProps = {
  children: React.ReactNode;
  className?: string;
  onInteractOutside?: () => void;
  /** @default "max-w-lg" */
  maxWidth?: string;
};

const ANIMATION_MS = 180;

export function DialogContent({
  children,
  className = "",
  onInteractOutside,
  maxWidth = "max-w-lg",
}: DialogContentProps) {
  const { open, onOpenChange, titleId, descriptionId } = useDialogContext();
  const depth = useContext(DialogDepthContext);
  // Each nesting level sits 10 above the previous (overlay/panel pair).
  const overlayZ = 50 + Math.max(0, depth - 1) * 10;
  const panelZ = overlayZ + 1;

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const restoredFocusRef = useRef<HTMLElement | null>(null);

  // Step 1 — mount on open, schedule unmount on close.
  // The cleanup clears any pending unmount timer when the dialog re-opens quickly.
  useEffect(() => {
    if (open) {
      restoredFocusRef.current = document.activeElement as HTMLElement;
      setMounted(true);
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), ANIMATION_MS);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Step 2 — trigger the enter transition.
  // useLayoutEffect fires synchronously after React commits the mounted=true DOM
  // update, guaranteeing the element is in the DOM at opacity:0 before we
  // schedule the rAF. The rAF then fires after the browser paints that first
  // invisible frame, giving the CSS transition something to animate from.
  useLayoutEffect(() => {
    if (!mounted || !open) return;
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [mounted, open]);

  // Restore focus when dialog closes
  useEffect(() => {
    if (!mounted) {
      restoredFocusRef.current?.focus();
      restoredFocusRef.current = null;
    }
  }, [mounted]);

  // Move focus inside the panel on open
  useEffect(() => {
    if (!visible || !panelRef.current) return;
    const focusable =
      panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    focusable[0]?.focus();
  }, [visible]);

  // Escape + focus trap
  useEffect(() => {
    if (!mounted) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onOpenChange(false);
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusable = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        );
        if (!focusable.length) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mounted, onOpenChange]);

  // Scroll lock
  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted]);

  if (!mounted) return null;

  const transition = `opacity ${ANIMATION_MS}ms ease, transform ${ANIMATION_MS}ms ease`;

  return createPortal(
    <>
      <div
        aria-hidden="true"
        onClick={onInteractOutside ?? (() => onOpenChange(false))}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: overlayZ,
          backgroundColor: "rgba(0,0,0,0.5)",
          transition: `opacity ${ANIMATION_MS}ms ease`,
          opacity: visible ? 1 : 0,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        ref={panelRef}
        className={`bg-primary rounded-lg border border-solid border-primary flex flex-col w-[calc(100%-2rem)] ${maxWidth} max-h-[90vh] ${className}`}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          zIndex: panelZ,
          transition,
          opacity: visible ? 1 : 0,
          transform: visible
            ? "translate(-50%, -50%) scale(1)"
            : "translate(-50%, -50%) scale(0.95)",
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Composition primitives
// ---------------------------------------------------------------------------

export type DialogHeaderProps = {
  children: React.ReactNode;
  className?: string;
};

export function DialogHeader({ children, className = "" }: DialogHeaderProps) {
  const { onOpenChange, showCloseButton, size } = useDialogContext();
  const py = size === "small" ? "py-md" : "py-xl";
  return (
    <div
      className={`shrink-0 flex items-center justify-between gap-sm px-2xl ${py} border-b border-solid border-primary ${className}`}
    >
      <div className="flex flex-col gap-xs flex-1 min-w-0">{children}</div>
      {showCloseButton && (
        <button
          type="button"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
          className="shrink-0 w-3xl h-3xl flex items-center justify-center bg-primary border border-solid border-primary rounded-lg cursor-pointer transition-colors duration-150 hover:border-brand"
        >
          <X size={16} className="text-primary" />
        </button>
      )}
    </div>
  );
}

export type DialogTitleProps = {
  children: React.ReactNode;
  className?: string;
};

export function DialogTitle({ children, className = "" }: DialogTitleProps) {
  const { titleId } = useDialogContext();
  return (
    <Headline id={titleId} size={HEADLINE_SIZES.SMALL} className={className}>
      {children}
    </Headline>
  );
}

export type DialogDescriptionProps = {
  children: React.ReactNode;
  className?: string;
};

export function DialogDescription({
  children,
  className = "",
}: DialogDescriptionProps) {
  const { descriptionId } = useDialogContext();
  return (
    <BodyText
      id={descriptionId}
      size={BODY_TEXT_SIZES.SMALL}
      color="text-secondary"
      className={className}
    >
      {children}
    </BodyText>
  );
}

export type DialogBodyProps = { children: React.ReactNode; className?: string };

/**
 * Scrollable content area between header and footer.
 *
 * `min-h-0` lets it shrink below its content height when the panel hits
 * `max-h-[90vh]` — without it a tall body pushes the footer out of the panel
 * on short viewports (small windows, browser zoom). Bodies that own an inner
 * scroller should stay flex-driven (`flex flex-col min-h-0`) rather than
 * pinning a fixed `min-h-[…]`, which reintroduces the overflow.
 */
export function DialogBody({ children, className = "" }: DialogBodyProps) {
  return (
    <div className={`flex-1 min-h-0 overflow-y-auto p-2xl ${className}`}>
      {children}
    </div>
  );
}

export type DialogFooterProps = {
  children: React.ReactNode;
  className?: string;
};

export function DialogFooter({ children, className = "" }: DialogFooterProps) {
  const { size } = useDialogContext();
  const py = size === "small" ? "py-md" : "py-lg";
  return (
    <div
      className={`shrink-0 flex items-center justify-end gap-sm px-2xl ${py} border-t border-solid border-primary ${className}`}
    >
      {children}
    </div>
  );
}
